import { SubscriptionType, SubscriptionPlan } from './gen/types'
import type { DefaultPlan } from 'cf-types'

export function isPaidSubscription(subscriptionType: SubscriptionType) {
  return (
    subscriptionType !== SubscriptionType.AllAccessMonthlyFreeTrial &&
    subscriptionType !== SubscriptionType.AllAccessYearlyFreeTrial &&
    subscriptionType !== SubscriptionType.StudioAiYearlyFreeTrial
  )
}

interface PaymentError {
  code?: string | null
  message?: string | null
}

function normalizeErrorValue(value: string | null | undefined) {
  return value
    ?.trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
}

export function isUserBanned(errors: PaymentError[] | null | undefined) {
  if (!errors?.length) {
    return false
  }

  return errors.some(error => {
    const normalizedMessage = normalizeErrorValue(error.message)
    return normalizedMessage === 'user_is_banned'
  })
}

export function isUserTrialing(errors: PaymentError[] | null | undefined) {
  if (!errors?.length) {
    return false
  }

  return errors.some(error => {
    const normalizedMessage = normalizeErrorValue(error.message)
    return normalizedMessage === 'trialing_subscription_found'
  })
}

export function isUserSubscribed(errors: PaymentError[] | null | undefined) {
  if (!errors?.length) {
    return false
  }

  return errors.some(error => {
    const normalizedMessage = normalizeErrorValue(error.message)
    return normalizedMessage === 'active_subscription_found'
  })
}

export function isCombinedCurrencies(errors: PaymentError[] | null | undefined) {
  if (!errors?.length) {
    return false
  }

  return errors.some(error => {
    const normalizedMessage = normalizeErrorValue(error.message)
    return normalizedMessage && normalizedMessage.includes('cannot_combine_currencies')
  })
}

export function mapSubscriptionTypeToSubscriptionPlan(
  subscriptionType: SubscriptionType,
): SubscriptionPlan {
  if (
    subscriptionType === SubscriptionType.AllAccessYearly ||
    subscriptionType === SubscriptionType.AllAccessYearlyPromo ||
    subscriptionType === SubscriptionType.AllAccessYearlyFreeTrial ||
    subscriptionType === SubscriptionType.AllAccessMonthly ||
    subscriptionType === SubscriptionType.AllAccessMonthlyFreeTrial
  ) {
    return SubscriptionPlan.AllAccessYearly
  }

  if (
    subscriptionType === SubscriptionType.StudioAiYearly ||
    subscriptionType === SubscriptionType.StudioAiYearlyFreeTrial
  ) {
    return SubscriptionPlan.StudioAiYearlyV2
  }
  if (subscriptionType === SubscriptionType.CoinPackMonthly25k) {
    return SubscriptionPlan.CoinPackMonthly25k
  }
  if (subscriptionType === SubscriptionType.CoinPackMonthly100k) {
    return SubscriptionPlan.CoinPackMonthly100k
  }
  if (subscriptionType === SubscriptionType.CoinPackMonthly250k) {
    return SubscriptionPlan.CoinPackMonthly250k
  }
  if (subscriptionType === SubscriptionType.CoinPackMonthly400k) {
    return SubscriptionPlan.CoinPackMonthly400k
  }
  if (subscriptionType === SubscriptionType.CoinPackMonthly700k) {
    return SubscriptionPlan.CoinPackMonthly700k
  }
  if (subscriptionType === SubscriptionType.CoinPackMonthly1M) {
    return SubscriptionPlan.CoinPackMonthly1M
  }

  return SubscriptionPlan.AllAccessYearly
}

/** Subscription type for `cf_subscription_type` after autostart (matches legacy free-trial URL values when `isTrial`). */
export function getAutostartSuccessSubscriptionTypeForUrl(
  subscriptionPlan: SubscriptionPlan,
  isTrial: boolean,
): SubscriptionType {
  if (isTrial && subscriptionPlan === SubscriptionPlan.AllAccessYearly) {
    return SubscriptionType.AllAccessYearlyFreeTrial
  }
  if (
    isTrial &&
    (subscriptionPlan === SubscriptionPlan.StudioAiYearlyV2 ||
      subscriptionPlan === SubscriptionPlan.StudioAiYearly)
  ) {
    return SubscriptionType.StudioAiYearlyFreeTrial
  }
  return mapSubscriptionPlanToSubscriptionType(subscriptionPlan)
}

export function mapSubscriptionPlanToSubscriptionType(
  subscriptionPlan: SubscriptionPlan,
): SubscriptionType {
  if (subscriptionPlan === SubscriptionPlan.AllAccessYearly) {
    return SubscriptionType.AllAccessYearly
  }
  if (
    subscriptionPlan === SubscriptionPlan.StudioAiYearlyV2 ||
    subscriptionPlan === SubscriptionPlan.StudioAiYearly
  ) {
    return SubscriptionType.StudioAiYearly
  }

  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly25k) {
    return SubscriptionType.CoinPackMonthly25k
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly100k) {
    return SubscriptionType.CoinPackMonthly100k
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly250k) {
    return SubscriptionType.CoinPackMonthly250k
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly400k) {
    return SubscriptionType.CoinPackMonthly400k
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly700k) {
    return SubscriptionType.CoinPackMonthly700k
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly1M) {
    return SubscriptionType.CoinPackMonthly1M
  }

  return SubscriptionType.AllAccessYearly
}

export function getDefaultPlan(subscriptionPlan: SubscriptionPlan, isTrial: boolean): DefaultPlan {
  if (
    subscriptionPlan === SubscriptionPlan.StudioAiYearlyV2 ||
    subscriptionPlan === SubscriptionPlan.StudioAiYearly
  ) {
    return 'aiStudio'
  }

  if (subscriptionPlan === SubscriptionPlan.AllAccessYearly) {
    if (isTrial) {
      return 'freeTrialYearly'
    }
    return 'yearly'
  }

  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly25k) {
    return 'coinPack25k'
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly100k) {
    return 'coinPack100k'
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly250k) {
    return 'coinPack250k'
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly400k) {
    return 'coinPack400k'
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly700k) {
    return 'coinPack700k'
  }
  if (subscriptionPlan === SubscriptionPlan.CoinPackMonthly1M) {
    return 'coinPack1M'
  }

  return 'yearly'
}

const COIN_PACK_SUBSCRIPTION_PLANS = new Set([
  SubscriptionPlan.CoinPackMonthly25k,
  SubscriptionPlan.CoinPackMonthly100k,
  SubscriptionPlan.CoinPackMonthly250k,
  SubscriptionPlan.CoinPackMonthly400k,
  SubscriptionPlan.CoinPackMonthly700k,
  SubscriptionPlan.CoinPackMonthly1M,
])

export const isCoinPack = (subscriptionPlan: SubscriptionPlan): boolean => {
  return COIN_PACK_SUBSCRIPTION_PLANS.has(subscriptionPlan)
}

export const isStudioAiSubscription = (subscriptionPlan: SubscriptionPlan): boolean => {
  return (
    subscriptionPlan === SubscriptionPlan.StudioAiYearly ||
    subscriptionPlan === SubscriptionPlan.StudioAiYearlyV2
  )
}

export const isAllAccessSubscription = (subscriptionPlan: SubscriptionPlan): boolean => {
  return subscriptionPlan === SubscriptionPlan.AllAccessYearly
}

/** Amplitude `subscription_type` payload (legacy free-trial slugs for all-access / studio-ai). */
export function mapSubscriptionPlanToAmplitudeSubscriptionCategory(
  subscriptionPlan: SubscriptionPlan,
): 'all-access' | 'studio-ai' | 'coins' {
  if (isAllAccessSubscription(subscriptionPlan)) {
    return 'all-access'
  }
  if (isStudioAiSubscription(subscriptionPlan)) {
    return 'studio-ai'
  }
  if (isCoinPack(subscriptionPlan)) {
    return 'coins'
  }
  return 'all-access'
}

/** Default marketing URL when starting a subscription while logged out (before optional `redirectUrl` override). */
export function getStartSubscriptionLoggedOutRedirectPath(
  subscriptionPlan: SubscriptionPlan,
  isTrial: boolean,
): string | null {
  if (isTrial) {
    if (subscriptionPlan === SubscriptionPlan.AllAccessYearly) {
      return '/b/free-trial/'
    }
    return '/b/free-trial-aistudio/'
  }
  if (isStudioAiSubscription(subscriptionPlan)) {
    return '/b/aistudio/'
  }
  if (isCoinPack(subscriptionPlan)) {
    return '/b/aistudio/'
  }
  if (subscriptionPlan === SubscriptionPlan.AllAccessYearly) {
    return '/b/yearly/'
  }
  return null
}
